import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Typography } from "@mui/material";

import APP_ROUTES from "../../../../../routers/routeConstants";
import { useProfileData } from "../../../../../hooks/useProfileData";

import { useTrendingServices } from "../../../api/queries/useTrendingServices";

import { ServiceCard, ServiceCardLoader } from "../../../components/ServiceCard";
import EmptyServicesFigure from "../../../assets/images/empty-services-figure.png";
import { ArrowRightButtonIcon, TrendingTitleIcon } from "../../../assets/icons/go-hire-icons";

import GoHireSlider from "../../../components/GoHireSlider";

const TrendingServices = () => {
  const { t } = useTranslation("goService", { keyPrefix: "goService" });
  const navigate = useNavigate();

  const {
    data: trendingServices,
    isLoading: isTrendingServicesLoading,
    isError: isTrendingServicesError,
  } = useTrendingServices({ limit: 6, page: 1 });
  const { data: userData, isLoading: isUserLoading } = useProfileData();

  const IS_LOADING = isTrendingServicesLoading || isUserLoading;

  const handleSeeAllServices = () => navigate(APP_ROUTES.TRENDING_SERVICES);

  if ((trendingServices?.items.length === 0 && !IS_LOADING) || isTrendingServicesError) {
    return <EmptyServicesList />;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>
          <TrendingTitleIcon />
          {t("trendingServices")}
        </Typography>
        <Button
          onClick={handleSeeAllServices}
          endIcon={<ArrowRightButtonIcon />}
          variant="text-md"
          sx={styles.seeAllButton}
        >
          {t("seeAllServices")}
        </Button>
      </Box>
      <Divider sx={styles.divider} />
      <GoHireSlider
        items={trendingServices?.items.map((service) => <ServiceCard key={service.id} service={service} />) || []}
        isLoading={IS_LOADING}
        loadingComponent={<ServiceCardLoader />}
        loadingCount={3}
        numberOfSlidesPerView={2}
      />
    </Box>
  );
};

export default TrendingServices;

const EmptyServicesList = () => {
  const { t } = useTranslation("goService", { keyPrefix: "goService" });
  return (
    <Box sx={styles.emptyContainer}>
      <Typography sx={styles.emptyTitle}>
        <TrendingTitleIcon />
        {t("trendingServices")}
      </Typography>
      <Divider orientation="horizontal" flexItem sx={styles.divider} />
      <Box sx={styles.emptyImage} component="img" src={EmptyServicesFigure} alt="Empty Reviews" />
      <Typography sx={styles.emptyText}>{t("noServices")}</Typography>
      {/* <Button variant="primary-md" color="primary" sx={styles.createButton}>
          {t("createService")}
        </Button> */}
    </Box>
  );
};

const styles = {
  container: {
    padding: "20px 24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  divider: {
    margin: "0 -24px",
    borderColor: "#EAECF0",
  },
  emptyContainer: {
    padding: "20px 24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    alignItems: "center",
  },
  emptyTitle: {
    width: "100%",
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
  },
  emptyImage: {
    width: "186px",
    height: "138.966px",
  },
  emptyText: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  createButton: {
    width: "fit-content",
  },
  seeAllButton: {
    padding: 0,
    background: "transparent !important",
  },
};
